import { z } from 'zod';
import { namePart } from './inputs.js';
import { email } from './email.js';
import { userId } from './user-id.js';
import { subjectCode } from './subject-code.js';
import { FeatureFlag, SUBJECT_CATEGORY_CODES } from '@athena/shared';
const id = z.string().min(1).max(64);
const name = z.string().trim().min(1).max(120);
const reason = z.string().trim().min(1).max(1000);
const version = z.number().int().nonnegative();
const roles = z
    .array(z.enum(['TUTOR', 'STUDENT', 'ADMIN']))
    .max(3)
    .refine((v) => new Set(v).size === v.length);
const page = {
    query: z.string().trim().max(120).default(''),
    offset: z.number().int().min(0).max(100000).default(0),
    limit: z.number().int().min(1).max(100).default(25),
};
export const adminUserListInput = z
    .object({
    ...page,
    role: z.enum(['TUTOR', 'STUDENT', 'ADMIN']).nullish(),
    disabled: z.boolean().nullish(),
    sort: z.enum(['NEWEST', 'EMAIL', 'NAME']).default('NEWEST'),
})
    .strict();
export const adminCreateUserInput = z
    .object({
    email,
    firstName: namePart,
    lastName: namePart,
    preferredLanguage: z.enum(['ES', 'EN']),
    roles: roles.refine((value) => value.length === 1),
    reason,
})
    .strict();
export const adminUpdateUserInput = z
    .object({
    userId,
    version,
    firstName: namePart,
    lastName: namePart,
    preferredLanguage: z.enum(['ES', 'EN']),
    roles,
    reason,
})
    .strict();
export const adminSetUserAccessInput = z
    .object({ userId, version, disabled: z.boolean(), reason })
    .strict();
export const adminSetUserFeatureFlagInput = z
    .object({
    userId,
    version,
    flag: z.enum(FeatureFlag),
    enabled: z.boolean(),
    reason,
})
    .strict();
/** The complete desired set; an empty array removes every assigned flag. */
export const adminSetUserFeatureFlagsInput = z
    .object({
    userId,
    version,
    flags: z
        .array(z.enum(FeatureFlag))
        .max(Object.keys(FeatureFlag).length)
        .refine((flags) => new Set(flags).size === flags.length),
    reason,
})
    .strict();
/**
 * `confirmedOtherStudentAttempts` is the number of other Students' attempts the
 * Admin was shown in the deletion preview. The server refuses a deletion that
 * would now destroy more than that, so third-party history is never removed
 * on a number nobody saw.
 */
export const adminDeleteUserInput = z
    .object({
    userId,
    version,
    reason,
    confirmedOtherStudentAttempts: z.number().int().nonnegative(),
})
    .strict();
export const adminSubjectListInput = z
    .object({
    ...page,
    active: z.boolean().nullish(),
    sort: z.enum(['CODE', 'NEWEST']).default('CODE'),
})
    .strict();
const categoryCodes = z
    .array(z.enum(SUBJECT_CATEGORY_CODES))
    .max(4)
    .refine((codes) => new Set(codes).size === codes.length)
    .optional();
const subjectNames = {
    categoryCodes,
    spanishName: name,
    englishName: name,
    active: z.boolean(),
    reason,
};
export const adminSetSubjectCategoryInput = z
    .object({ code: z.enum(SUBJECT_CATEGORY_CODES), enabled: z.boolean(), version, reason })
    .strict();
export const adminCreateSubjectInput = z
    .object({
    code: subjectCode,
    ...subjectNames,
})
    .strict();
export const adminUpdateSubjectInput = z
    .object({ subjectId: id, version, ...subjectNames })
    .strict();
export const adminDeleteSubjectInput = z.object({ subjectId: id, version, reason }).strict();
//# sourceMappingURL=management.js.map